"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import SectionHeading from "@/components/SectionHeading";
import { ArrowUp, Award, Code, Briefcase, Users } from "lucide-react";

interface ImpactStat {
  value: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const impactStats: ImpactStat[] = [
  {
    value: "40+",
    label: "Projects Delivered",
    icon: <Code className="w-6 h-6 text-primary" />,
    description: "Built and deployed scalable solutions for startups and enterprises"
  },
  {
    value: "15M+",
    label: "Users Impacted",
    icon: <Users className="w-6 h-6 text-primary" />,
    description: "Created applications that enhance daily experiences"
  },
  {
    value: "65%",
    label: "Cost Reduction",
    icon: <ArrowUp className="w-6 h-6 text-primary" />,
    description: "Optimized systems to significantly lower operational costs"
  },
  {
    value: "6+",
    label: "Years Experience",
    icon: <Briefcase className="w-6 h-6 text-primary" />,
    description: "Specialized in Elixir, Rails, Node, and React ecosystems"
  }
];

const skills = [
  "AI Engineering",
  "LLM Integration",
  "Full-Stack Development", 
  "Cloud Architecture",
  "Team Leadership"
];

const AboutImpact = () => {
  return (
    <section id="about" className="py-20 px-6">
      <div className="container max-w-6xl mx-auto">
        <SectionHeading title="Why me?" />
        
        <div className="flex flex-col md:flex-row gap-10 mb-16 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
            className="md:w-1/3 relative will-change-transform backface-hidden"
          >
            <div className="absolute inset-0 bg-accent/20 rounded-2xl -rotate-6 transform" />
            <div className="absolute inset-0 bg-secondary rounded-2xl rotate-3 transform" />
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden border border-border bg-card">
              <Image 
                src="/aqdas.webp" 
                alt="Aqdas Malik" 
                fill
                className="object-cover"
                priority
              />
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
            className="md:w-2/3 will-change-transform backface-hidden"
          >
            <div>
              <p className="text-lg leading-relaxed mb-6">
                With over 6 years of experience, I&apos;m a seasoned engineer specializing in Elixir, Rails, Node, and React ecosystems. 
                I&apos;ve architected and deployed solutions on AWS/GCP that scale to millions of users, combining technical expertise with 
                mentorship skills to accelerate team growth and product delivery.
              </p>
              
              <p className="text-lg leading-relaxed mb-8">
                My focus is on creating robust, maintainable systems that solve real business problems through intelligent automation 
                and thoughtful architecture. The measurable impact of my work spans from cost reduction to performance optimization.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-8">
                {skills.map((skill, index) => (
                  <motion.span 
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    viewport={{ once: true }}
                    className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm will-change-transform backface-hidden"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 text-primary will-change-transform backface-hidden"
              >
                <Award className="w-5 h-5" />
                <span className="font-semibold">Award-winning solutions recognized in the tech industry</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {impactStats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 will-change-transform backface-hidden"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="bg-primary/10 p-2 rounded-full">
                  {stat.icon}
                </div>
                <motion.h3
                  initial={{ scale: 0.8 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  viewport={{ once: true }}
                  className="text-2xl font-bold"
                >
                  {stat.value}
                </motion.h3>
              </div>
              <h4 className="text-lg font-medium mb-1">{stat.label}</h4>
              <p className="text-sm text-muted-foreground">{stat.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutImpact; 